import "./Login.scss";
import "react-toastify/dist/ReactToastify.css";
import  { useState, useEffect } from "react";
import {
  FormControl,
  IconButton,
  InputAdornment,
  Modal,
  OutlinedInput,
  TextField,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Link } from "react-router-dom";
import OtpInput from "react-otp-input";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../utils/api";
import Facebook from "../../assets/images/login-facebook.svg";
import Google from "../../assets/images/login-google.svg";
import Apple from "../../assets/images/login-apple.svg";

// Import images
import Logo from "./../../assets/images/MainLogo.svg";
import GreenButton from "../../components/GreenButton/GreenButton";
import { validatePhone } from "../../utils/validatePhone";

const Login = ({ open, onClose, initialView = "login" }) => {
  const { login } = useAuth();
  const { t, dir } = useLanguage();
  const [currentView, setCurrentView] = useState(initialView);
  const [otp, setOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agree, setAgree] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordResetFlow, setIsPasswordResetFlow] = useState(false);
  const [resetOtp, setResetOtp] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [canResend, setCanResend] = useState(true);

  // Function to reset form data when changing views
  const resetFormData = (view) => {
    // Base reset - everything empty
    let newFormData = {
      name: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
    };

    // Keep only phone number when going to activeCode or enterNewPassword
    if (view === "activeCode" || view === "enterNewPassword") {
      newFormData.phoneNumber = formData.phoneNumber;
    }

    setFormData(newFormData);
    setErrors({});

    // Reset agreement checkbox when switching to register
    if (view === "register") {
      setAgree(false);
    }

    // Reset password reset flow flag when switching to a view other than activeCode
    // for password reset flow
    if (view !== "activeCode" && view !== "enterNewPassword") {
      setIsPasswordResetFlow(false);
      setResetOtp("");
    }

    // Reset OTP when switching to activeCode
    if (view === "activeCode") {
      setOtp("");
    }
  };

  // Effect for handling countdown timer
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prevCount) => prevCount - 1);
      }, 1000);
      setCanResend(false);
    } else if (countdown === 0 && !canResend) {
      setCanResend(true);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [countdown, canResend]);

  // Format countdown time to minutes:seconds
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" + secs : secs}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    // Clear error when field is edited
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };



  const validateForm = (type) => {
    const newErrors = {};

    if (type === "login" || type === "register" || type === "forgetPassword") {
      if (!formData.phoneNumber) {
        newErrors.phoneNumber = t("phoneRequired");
      } else if (!validatePhone(formData.phoneNumber)) {
        newErrors.phoneNumber = t("phoneInvalid");
      }
    }

    if (type === "login") {
      if (!formData.password) {
        newErrors.password = t("passwordRequired");
      }
    }

    if (type === "register") {
      if (!formData.name) {
        newErrors.name = t("nameRequired");
      }

      if (!formData.password) {
        newErrors.password = t("passwordRequired");
      } else if (formData.password.length < 6) {
        newErrors.password = t("passwordMinLength");
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = t("confirmPasswordRequired");
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t("passwordsNotMatch");
      }

      // Check if user agreed to terms and conditions
      if (!agree) {
        newErrors.agree = t("agreeRequired");
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm("login")) return;

    setIsLoading(true);

    try {
      // Attempt login directly
      const response = await api.post("/auth/users/login", {
        phoneNumber: formData.phoneNumber,
        password: formData.password,
      });

      // Check response directly before using the login function
      if (response.data.status === "success") {
        const success = await login({
          phoneNumber: formData.phoneNumber,
          password: formData.password,
        });
        if (success) {
          onClose();
        }
      }
    } catch (error) {
      // Check specifically if this is a not-verified user
      if (
        error.response?.data?.status === "fail" &&
        error.response?.data?.isVerified === false
      ) {
        // Store the phone number for verification
        setFormData((prev) => ({
          ...prev,
          phoneNumber: formData.phoneNumber,
        }));

        // Switch to activeCode view immediately
        resetFormData("activeCode");
        setCurrentView("activeCode");

        // Enable the resend button
        setCanResend(true);
      } else {
        const message = error.response?.data?.message || t("loginError");
        setErrors({ general: message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    // This is the key fix - ensure the validation is properly triggered and errors are set
    const isValid = validateForm("register");
    if (!isValid) return;

    setIsLoading(true);
    try {
      const response = await api.post("/auth/users/signup", {
        name: formData.name,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      });

      // If success, move to the activeCode view but don't auto-request OTP
      if (response.data.status === "success") {
        toast.success(t("registrationSuccess"));

        // Ensure we move to activeCode view
        setTimeout(() => {
          resetFormData("activeCode");
          setCurrentView("activeCode");
          // Start countdown automatically for registration flow
          startCountdown();
        }, 0);
      }
    } catch (error) {
      // Check if the error indicates user registered but not verified
      if (
        error.response?.data?.status === "fail" &&
        error.response?.data?.isVerified === false
      ) {
        toast.info(t("accountExistsNotVerified"));

        // Move to verification code screen without requesting OTP
        setTimeout(() => {
          resetFormData("activeCode");
          setCurrentView("activeCode");
          setCanResend(true);
        }, 0);
      }
      // Check if the user is already registered and verified
      else if (
        error.response?.data?.status === "fail" &&
        error.response?.data?.isVerified === true
      ) {
        setErrors({ general: t("accountAlreadyExists") });
      } else {
        const message = error.response?.data?.message || t("registrationError");
        setErrors({ general: message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 4) {
      setErrors({ otp: t("otpRequired") });
      return;
    }

    setIsLoading(true);
    try {
      // Check if this is a password reset flow or regular verification
      if (isPasswordResetFlow) {
        // Call the password reset verification endpoint
        const response = await api.post("/auth/users/forgetPassword/verify", {
          otp,
          phoneNumber: formData.phoneNumber,
        });

        if (response.data.status === "success") {
          toast.success(t("verificationSuccess"));
          // Store the OTP for the final password reset step
          setResetOtp(otp);
          // Move to the password reset screen
          resetFormData("enterNewPassword");
          setCurrentView("enterNewPassword");
        }
      } else {
        // Regular OTP verification flow
        const response = await api.patch("/auth/users/verifyOTP", {
          otp,
          phoneNumber: formData.phoneNumber,
        });

        if (response.data.status === "success") {
          // Since we have the same response structure as login,
          // we can use the login function directly with the received data
          const loginSuccess = await login({
            // We're using the login function but passing the response data
            // instead of making another API call
            _verificationResponse: response.data,
          });

          if (loginSuccess) {
            toast.success(t("verificationSuccess"));
            onClose(); // Close the modal after successful login
          }
        }
      }
    } catch (error) {
      // Handle rate limit error (429)
      if (error.response?.status === 429) {
        setErrors({
          otp:
            t("rateLimitExceeded") ||
            "You have exceeded the 5 requests in 1 hour limit!",
        });
      } else {
        const message = error.response?.data?.message || t("verificationError");
        setErrors({ otp: message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to start the countdown
  const startCountdown = () => {
    // 10 minutes in seconds
    setCountdown(600);
    setCanResend(false);
  };

  const handleResendOTP = async () => {
    // Don't proceed if countdown is active
    if (!canResend) return;

    setIsLoading(true);
    try {
      // Use the appropriate endpoint based on the current flow
      const endpoint = isPasswordResetFlow
        ? "/auth/users/forgetPassword"
        : "/auth/users/requestOtp";

      const response = await api.post(endpoint, {
        phoneNumber: formData.phoneNumber,
      });

      if (response.data.status === "success") {
        toast.info(t("resendSuccess"));
        // Start the 10-minute countdown only after successful OTP request
        startCountdown();
      }
    } catch (error) {
      // Handle rate limit error (429)
      if (error.response?.status === 429) {
        setErrors({
          otp:
            t("rateLimitExceeded") ||
            "You have exceeded the 5 requests in 1 hour limit!",
        });
      } else {
        const message = error.response?.data?.message || t("resendError");
        setErrors({ otp: message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm("forgetPassword")) return;

    setIsLoading(true);
    try {
      // For password reset, we still need to request OTP initially as this is an expected behavior
      const response = await api.post("/auth/users/forgetPassword", {
        phoneNumber: formData.phoneNumber,
      });

      if (response.data.status === "success") {
        toast.success(t("resendSuccess"));
        // Set the password reset flow flag to true
        setIsPasswordResetFlow(true);
        // OTP successfully requested, start countdown and move to verification view
        startCountdown();
        resetFormData("activeCode");
        setCurrentView("activeCode");
      }
    } catch (error) {
      // Handle rate limit error (429)
      if (error.response?.status === 429) {
        setErrors({
          phoneNumber:
            t("rateLimitExceeded") ||
            "You have exceeded the 5 requests in 1 hour limit!",
        });
      } else {
        const message = error.response?.data?.message || t("phoneInvalid");
        setErrors({ phoneNumber: message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();

    if (!formData.password) {
      setErrors({ password: t("passwordRequired") });
      return;
    }

    if (formData.password.length < 6) {
      setErrors({ password: t("passwordMinLength") });
      return;
    }

    if (!formData.confirmPassword) {
      setErrors({ confirmPassword: t("confirmPasswordRequired") });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setErrors({ confirmPassword: t("passwordsNotMatch") });
      return;
    }

    setIsLoading(true);
    try {
      // Use the PATCH method with the correct endpoint and include the OTP
      const response = await api.patch("/auth/users/resetPassword", {
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        otp: resetOtp,
      });

      if (response.data.status === "success") {
        toast.success(t("passwordResetSuccess"));
        resetFormData("login");
        setCurrentView("login");
      }
    } catch (error) {
      const message = error.response?.data?.message || t("resendError");
      setErrors({ general: message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const renderErrorMessage = (field) => {
    return errors[field] ? (
      <div
        className="error-message"
        style={{ color: "#e51937", fontSize: "12px", marginTop: "5px" }}
      >
        {errors[field]}
      </div>
    ) : null;
  };

  // const handleFacebookLogin = async () => {
  //   try {
  //     sessionStorage.setItem('returnUrl', window.location.pathname);
  //     window.location.href = 'https://api.houfalmanazel.com/api/v1/auth/facebook';
  //   } catch (error) {
  //     console.log(error);
  //     toast.error(t("socialLoginError") || "Social login failed");
  //   }
  // };

  const handleGoogleLogin = () => {
    sessionStorage.setItem('returnUrl', window.location.pathname);
    window.location.href = 'https://api.houfalmanazel.com/api/v1/auth/google';
  };

  const handleAppleLogin = async () => {
    try {
      sessionStorage.setItem('returnUrl', window.location.pathname);
      window.location.href = 'https://api.houfalmanazel.com/api/v1/auth/apple';
    } catch (error) {
      console.log(error);
      toast.error(t("socialLoginError") || "Social login failed");
    }
  };

  const renderPasswordField = (label, name, showState, toggleShowFn) => (
    <FormControl sx={{ m: 1, flex: 1, width: "100%" }} variant="outlined">
      <label className="form-label">{label}</label>
      <OutlinedInput
        type={showState ? "text" : "password"}
        name={name}
        value={formData[name] || ""}
        onChange={handleChange}
        error={!!errors[name]}
        endAdornment={
          <InputAdornment position="start">
            <IconButton
              onClick={toggleShowFn}
              onMouseDown={handleMouseDownPassword}
              edge="end"
            >
              {showState ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        }
      />
      {renderErrorMessage(name)}
    </FormControl>
  );

  const renderTextField = (label, name) => (
    <FormControl sx={{ m: 1, flex: 1, width: "100%" }}>
      <label className="form-label">{label}</label>
      <TextField
        fullWidth
        className="form-input"
        required
        name={name}
        value={formData[name] || ""}
        onChange={handleChange}
        error={!!errors[name]}
        placeholder={name === "phoneNumber" ? "05XXXXXXXX" : ""}
      />
      {renderErrorMessage(name)}
    </FormControl>
  );

  const renderLoginView = () => (
    <div className="login" dir={dir}>
      <img src={Logo} alt="Logo" />
      <p>{t("login_title")}</p>
      {errors.general && (
        <div
          className="error-message"
          style={{ color: "#e51937", marginBottom: "10px" }}
        >
          {errors.general}
        </div>
      )}
      <form onSubmit={handleLoginSubmit}>
        {renderTextField(t("phoneNumber"), "phoneNumber")}
        {renderPasswordField(
          t("password"),
          "password",
          showPassword,
          handleClickShowPassword
        )}

        <FormControl
          sx={{ m: 1, flex: 1, width: "100%" }}
          variant="outlined"
          className="checkbox-group"
        >
          <div className="form-control">
            <input type="checkbox" />
            <span>{t("rememberMe")}</span>
          </div>
          <Link
            style={{ color: "#e51937", fontSize: "18px" }}
            onClick={() => {
              resetFormData("forgetPassword");
              setCurrentView("forgetPassword");
            }}
          >
            {t("forgotPassword")}
          </Link>
        </FormControl>

        <GreenButton
          text={isLoading ? t("loading") : t("login")}
          size="full"
          variant="fill"
          type="submit"
          disabled={isLoading}
        />

        <div className="to-register">
          <span>{t("dontHaveAccount")}</span>
          <Link
            style={{ color: "#5cb85c" }}
            onClick={() => {
              resetFormData("register");
              setCurrentView("register");
            }}
          >
            {t("createOne")}
          </Link>
        </div>
      </form>
      {/* <div className="another">{dir ==="rtl" ? "أو تسجيل الدخول باستخدام" : "or Login With" }</div> */}
        <div className="social-login-buttons">
          {/* <button
            type="button"
            className="social-btn facebook-btn"
            onClick={handleFacebookLogin}
            disabled={isLoading}
          >
            <img src={Facebook} alt="Facebook" />
              <span>{dir ==="rtl" ? "المتابعة باستخدام فيسبوك" : "Continue With Facebook"}</span>
          </button> */}

          <button
            type="button"
            className="social-btn google-btn"
            onClick={handleGoogleLogin}
            disabled={isLoading}
          >
            <img src={Google} alt="Google" />
            <span>{dir ==="rtl" ? "المتابعة باستخدام جوجل" : "Continue With Google"}</span>
          </button>

          <button
            type="button"
            className="social-btn apple-btn"
            onClick={handleAppleLogin}
            disabled={isLoading}
          >
            <img src={Apple} alt="Apple" />
                   <span>{dir ==="rtl" ? "المتابعة باستخدام ابل" : "Continue With Apple"}</span>
          </button>
        </div>
    </div>
  );

  const renderRegisterView = () => (
    <div className="register" dir={dir}>
      <img src={Logo} alt="Logo" />
      <p>{t("createAccount")}</p>
      {errors.general && (
        <div
          className="error-message"
          style={{ color: "#e51937", marginBottom: "10px" }}
        >
          {errors.general}
        </div>
      )}
      <form onSubmit={handleRegisterSubmit}>
        {renderTextField(t("fullName"), "name")}
        {renderTextField(t("phoneNumber"), "phoneNumber")}
        {renderPasswordField(
          t("password"),
          "password",
          showPassword,
          handleClickShowPassword
        )}
        {renderPasswordField(
          t("confirmPassword"),
          "confirmPassword",
          showConfirmPassword,
          handleClickShowConfirmPassword
        )}

        <FormControl
          sx={{ m: 1, flex: 1, width: "100%" }}
          variant="outlined"
          className="checkbox-group"
          required
        >
          <div className="form-control">
            <input
              type="checkbox"
              style={{ width: "20px", cursor: "pointer" }}
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
            />
            <span style={{ color: "#777", fontSize: "13px" }}>
              {t("iHaveReadAndAgree")}{" "}
              <Link
                style={{ color: "#5cb85c", fontSize: "13px" }}
                onClick={() => onClose()}
                to="/terms"
              >
                {t("termsAndConditions")}
              </Link>
            </span>
          </div>
          {renderErrorMessage("agree")}
        </FormControl>

        <GreenButton
          text={isLoading ? t("loading") : t("createAccount")}
          size="full"
          variant="fill"
          type="submit"
          disabled={isLoading}
        />

        <div className="to-register">
          <span>{t("alreadyHaveAccount")}</span>
          <Link
            style={{ color: "#5cb85c" }}
            onClick={() => {
              resetFormData("login");
              setCurrentView("login");
            }}
          >
            {t("login")}
          </Link>
        </div>
      </form>
    </div>
  );

  const renderForgetPasswordView = () => (
    <div className="forget-password" dir={dir}>
      <img src={Logo} alt="Logo" />
      <p>{t("forgotPassword")}</p>
      <span>{t("resetPasswordIntro")}</span>
      {errors.general && (
        <div
          className="error-message"
          style={{ color: "#e51937", marginBottom: "10px" }}
        >
          {errors.general}
        </div>
      )}
      <form onSubmit={handleForgetPasswordSubmit}>
        {renderTextField(t("phoneNumber"), "phoneNumber")}
        <GreenButton
          text={isLoading ? t("loading") : t("next")}
          size="full"
          variant="fill"
          type="submit"
          disabled={isLoading}
        />
      </form>
        <div className="to-register">
          <span>{t("alreadyHaveAccount")}</span>
          <Link
            style={{ color: "#5cb85c" }}
            onClick={() => {
              resetFormData("login");
              setCurrentView("login");
            }}
          >
            {t("login")}
          </Link>
        </div>
    </div>
  );

  const renderActiveCodeView = () => (
    <div className="active-code" dir={dir}>
      <img src={Logo} alt="Logo" />
      <p>{t("verificationCode")}</p>
      {errors.otp && (
        <div
          className="error-message"
          style={{ color: "#e51937", marginBottom: "10px" }}
        >
          {errors.otp}
        </div>
      )}
      <form onSubmit={handleVerifyOTP}>
        <OtpInput
          value={otp}
          onChange={setOtp}
          numInputs={4}
          renderInput={(props) => <input {...props} dir="ltr" />}
          containerStyle={{
            gap: "10px",
            direction: "ltr",
          }}
          inputStyle={{
            width: "50px",
            height: "50px",
            margin: "0 5px",
            fontSize: "24px",
            borderRadius: "5px",
            border: "1px solid #ddd",
            textAlign: "center",
            direction: "ltr",
          }}
        />
        <div
          className="timer"
          style={{
            margin: "15px 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {countdown > 0 ? (
            <div className="countdown-timer" style={{ color: "#5cb85c" }}>
              {t("timeRemaining")}: {formatTime(countdown)}
            </div>
          ) : (
            <Link
              className="re-send"
              style={{
                color: canResend ? "#5cb85c" : "#aaa",
                cursor: canResend ? "pointer" : "not-allowed",
                textDecoration: "none",
              }}
              onClick={canResend ? handleResendOTP : null}
            >
              {isLoading ? t("loading") : t("resendCode")}
            </Link>
          )}
        </div>

        <GreenButton
          text={isLoading ? t("loading") : t("next")}
          size="full"
          variant="fill"
          type="submit"
          disabled={isLoading}
        />
      </form>
    </div>
  );

  const renderEnterNewPasswordView = () => (
    <div className="enter-new-password" dir={dir}>
      <img src={Logo} alt="Logo" />
      <p>{t("newPassword")}</p>
      <span>{t("enterNewPassword")}</span>
      {errors.general && (
        <div
          className="error-message"
          style={{ color: "#e51937", marginBottom: "10px" }}
        >
          {errors.general}
        </div>
      )}
      <form onSubmit={handleResetPasswordSubmit}>
        {renderPasswordField(
          t("password"),
          "password",
          showPassword,
          handleClickShowPassword
        )}
        {renderPasswordField(
          t("confirmPassword"),
          "confirmPassword",
          showConfirmPassword,
          handleClickShowConfirmPassword
        )}
        <GreenButton
          text={isLoading ? t("loading") : t("next")}
          size="full"
          variant="fill"
          type="submit"
          disabled={isLoading}
        />
      </form>
    </div>
  );

  const views = {
    login: renderLoginView,
    register: renderRegisterView,
    forgetPassword: renderForgetPasswordView,
    activeCode: renderActiveCodeView,
    enterNewPassword: renderEnterNewPasswordView,
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      {views[currentView]()}
    </Modal>
  );
};

export default Login;